import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { getTemplateById } from '../data/templates';
import { products } from '../data/products';
import PremiumPaywall from '../components/PremiumPaywall';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Sparkles, ArrowRight, ArrowLeft, Loader2, CheckCircle2,
  Crown, Home, RefreshCw, Wand2, GripVertical,
  Plus, X, List, Lock, ShoppingBag, Eye
} from 'lucide-react';
import VisualProductView from '../components/VisualProductView';
import DocumentProductView from '../components/DocumentProductView';
import UserNavMenu from '../components/UserNavMenu';
import LumeraLogo from '../components/LumeraLogo';
import { productTypeConfig, contentStructureTemplates, inspirationChips } from '../data/productTypes';
import { hasCustomization } from '../data/templateCustomizationSchemas';
import { hasPersonalization } from '../data/productPersonalizationSchemas';
import { toneOptions } from '../mock/mockData';

import { AutosaveIndicator } from '../components/AutosaveIndicator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Suggestion chips component for audience field
const SuggestionChips = ({ suggestions, onSelect, currentValue }) => {
  if (!suggestions || suggestions.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {suggestions.slice(0, 4).map((suggestion, idx) => (
        <button
          key={idx}
          type="button"
          onClick={() => onSelect(suggestion)}
          className={`text-xs font-sans px-3 py-1.5 rounded-full border transition-all ${
            currentValue === suggestion
              ? 'bg-primary/10 border-primary text-primary'
              : 'bg-stone-50 border-stone-200 text-stone-500 hover:border-primary/30 hover:text-stone-700'
          }`}
        >
          {suggestion}
        </button>
      ))}
    </div>
  );
};

// Field suggestions based on product type
const fieldSuggestions = inspirationChips;


/* ═══════════════════════════════════════════════════
   GENERATOR COMPONENT — 6-step AI Product Studio
   ═══════════════════════════════════════════════════ */
const Generator = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user, isPro, loading: authLoading, hasProductAccess, fetchPurchases, purchasesLoaded } = useAuth();

  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [formData, setFormData] = useState({
    topic: '',
    audience: '',
    painPoint: '',
    tone: 'friendly',
    language: 'en',
    length: 'medium',
    outputSize: 'a4-document'
  });
  const [contentStructure, setContentStructure] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadedProjectId, setLoadedProjectId] = useState(null);

  // Preview mode state
  const [previewAccess, setPreviewAccess] = useState(null); // null | 'full' | 'preview'
  const [previewProduct, setPreviewProduct] = useState(null);
  const [previewSteps, setPreviewSteps] = useState(3);
  const [showPaywall, setShowPaywall] = useState(false);
  const autosaveTimer = useRef(null);
  const draftLoaded = useRef(false);

  const productSlug = searchParams.get('product');

  // ── Check product access (preview vs full) ──
  useEffect(() => {
    if (authLoading || !user || !productSlug || !purchasesLoaded) return;

    const checkAccess = async () => {
      try {
        const res = await axios.get(`${API}/purchases/preview-access/${productSlug}`);
        const { access, product } = res.data;
        setPreviewAccess(access);
        setPreviewProduct(product);
        setPreviewSteps(product.preview_steps || 3);
        setShowPaywall(false);
      } catch {
        // Product not in catalog — allow free access
        setPreviewAccess('full');
      }
    };
    checkAccess();
  }, [productSlug, authLoading, user, purchasesLoaded]);

  // ── Load saved draft ──
  useEffect(() => {
    if (!user || !productSlug || draftLoaded.current || authLoading) return;
    draftLoaded.current = true;

    const loadDraft = async () => {
      try {
        const res = await axios.get(`${API}/drafts/${productSlug}`);
        const draft = res.data?.draft;
        if (draft && !generatedContent) {
          if (draft.form_data) {
            setFormData(prev => ({ ...prev, ...draft.form_data }));
          }
          if (draft.content_structure?.length > 0) {
            setContentStructure(draft.content_structure);
          }
          if (draft.selected_type_id) {
            const typeConfig = productTypeConfig.find(t => t.id === draft.selected_type_id);
            if (typeConfig) setSelectedType(typeConfig);
          }
          // Don't restore step beyond preview limit for preview users
          if (draft.step && draft.step > 1) {
            setStep(draft.step);
          }
          toast('Welcome back', { description: 'We restored your saved progress.', icon: '✨' });
        }
      } catch {
        // No draft — fine
      }
    };
    loadDraft();
  }, [user, productSlug, authLoading, generatedContent]);

  // ── Autosave draft ──
  const saveDraft = useCallback(async () => {
    if (!user || !productSlug || step >= 6) return;
    try {
      await axios.post(`${API}/drafts/save`, {
        product_slug: productSlug,
        step,
        form_data: formData,
        content_structure: contentStructure,
        selected_type_id: selectedType?.id || null,
      });
    } catch {
      // Silent fail — non-critical
    }
  }, [user, productSlug, step, formData, contentStructure, selectedType]);

  // Autosave on form changes (debounced)
  useEffect(() => {
    if (!productSlug || step >= 6) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(saveDraft, 2000);
    return () => clearTimeout(autosaveTimer.current);
  }, [formData, contentStructure, step, saveDraft, productSlug]);

  // ── Preview step gate: check if user can proceed ──
  const canProceedToStep = useCallback((targetStep) => {
    // Full access (Pro, purchased, or no product slug) → always proceed
    if (previewAccess === 'full' || !productSlug) return true;
    // Preview mode → can only go up to previewSteps
    if (previewAccess === 'preview' && targetStep > previewSteps) return false;
    return true;
  }, [previewAccess, previewSteps, productSlug]);

  const handleStepChange = useCallback((targetStep) => {
    // mode-select counts as step 4 for preview purposes
    const numericTarget = targetStep === 'mode-select' ? 4 : targetStep;
    if (!canProceedToStep(numericTarget)) {
      setShowPaywall(true);
      return;
    }
    setShowPaywall(false);
    setStep(targetStep);
  }, [canProceedToStep]);

  const stepLabels = ['Idea', 'Audience', 'Product type', 'Style', 'Content', 'Result'];

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }

    // Redirect to personalization wizard if product has a schema
    if (productSlug && hasPersonalization(productSlug) && !generatedContent) {
      navigate(`/personalize?product=${productSlug}`, { replace: true });
      return;
    }

    const projectId = searchParams.get('project_id');
    if (projectId && !generatedContent) {
      (async () => {
        try {
          const res = await axios.get(`${API}/projects/${projectId}`);
          const project = res.data;
          const typeConfig = productTypeConfig.find(t => t.id === project.product_type);
          setSelectedType(typeConfig || { id: project.product_type, name: project.product_type });
          setGeneratedContent(project.content);
          setLoadedProjectId(projectId);
          setStep(6);
        } catch (err) {
          setError('Could not load project.');
        }
      })();
      return;
    }

    const templateId = searchParams.get('template');
    if (templateId) {
      const template = getTemplateById(templateId);
      if (template) {
        setActiveTemplate(template);
        const typeConfig = productTypeConfig.find(t => t.id === template.preFill.productType);
        setSelectedType(typeConfig);
        setFormData(prev => ({ ...prev, tone: template.preFill.tone, length: template.preFill.length }));
        setStep(1);
      }
    }

    // Prefill from homepage AI assistant
    const prefillType = searchParams.get('type');
    const prefillTopic = searchParams.get('prefill_topic');
    if (!templateId && prefillType) {
      const typeConfig = productTypeConfig.find(t => t.id === prefillType);
      if (typeConfig) {
        setSelectedType(typeConfig);
        if (prefillTopic) {
          setFormData(prev => ({ ...prev, topic: decodeURIComponent(prefillTopic) }));
          // Pre-set content structure for the type
          setContentStructure((contentStructureTemplates[prefillType] || []).map(s => ({ ...s })));
          setStep(2); // Go to audience step (topic already filled)
        } else {
          setStep(3);
        }
      }
    }

    if (location.state?.remixTemplate && location.state?.templateStructure) {
      const { productType, templateName } = location.state;
      const typeConfig = productTypeConfig.find(t => t.id === productType);
      if (typeConfig) setSelectedType(typeConfig);
      setFormData(prev => ({ ...prev, topic: `(Remix av "${templateName}")` }));
      setStep(1);
      window.history.replaceState({}, document.title);
    }
  }, [user, searchParams, authLoading, navigate, generatedContent, location.state]);
  // Deps: API, axios, productTypeConfig, contentStructureTemplates are module-level constants

  // When product type changes, load structure template
  useEffect(() => {
    if (selectedType && contentStructure.length === 0) {
      const tmpl = contentStructureTemplates[selectedType.id] || [];
      setContentStructure(tmpl.map(s => ({ ...s })));
    }
  }, [selectedType, contentStructure.length]);

  const handleGenerate = async () => {
    if (!selectedType || !formData.topic || !formData.audience) {
      setError('Fill in all required fields.');
      return;
    }
    setError('');
    setGenerating(true);
    try {
      const payload = {
        productType: selectedType.id,
        topic: formData.topic,
        audience: formData.audience,
        painPoint: formData.painPoint,
        tone: formData.tone,
        language: formData.language,
        length: formData.length,
        outputSize: formData.outputSize,
        slideCount: activeTemplate ? activeTemplate.preFill.slideCount : 8,
        templateStructure: activeTemplate ? activeTemplate.preFill.structure : null,
        templateName: activeTemplate ? activeTemplate.name : null,
        contentStructure: contentStructure.map(s => s.title),
        product_slug: productSlug || null,
      };
      // Start async generation — returns task_id immediately
      const startRes = await axios.post(`${API}/content/generate`, payload, { timeout: 15000 });
      const taskId = startRes.data.task_id;

      // Poll for result every 3 seconds
      let attempts = 0;
      const maxAttempts = 60; // 3 minutes max
      let consecutive404s = 0;
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 3000));
        attempts++;
        try {
          const pollRes = await axios.get(`${API}/content/tasks/${taskId}?_t=${Date.now()}`, { timeout: 10000 });
          consecutive404s = 0; // Reset on success
          if (pollRes.data.status === 'complete') {
            setGeneratedContent(pollRes.data.result);
            setStep(6);
            setGenerating(false);
            return;
          } else if (pollRes.data.status === 'failed') {
            setError(pollRes.data.error || 'Generation failed. Please try again.');
            setGenerating(false);
            return;
          }
          // status === 'pending' → continue polling
        } catch (pollErr) {
          // 404 = task might not be ready yet or was archived — retry up to 5 times
          if (pollErr.response?.status === 404) {
            consecutive404s++;
            if (consecutive404s >= 5) throw pollErr; // Truly gone
            continue;
          }
          // Other network error — retry until maxAttempts
          if (attempts >= maxAttempts) throw pollErr;
        }
      }
      setError('Generation took too long. The AI server may be busy — please try again.');
    } catch (err) {
      if (err.response?.status === 403) {
        // Usage limit exceeded or premium product not purchased — show paywall
        setShowPaywall(true);
        setGenerating(false);
        return;
      }
      let msg = 'Generation failed. Please try again.';
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        msg = 'Generation took too long. The AI server may be busy — please try again.';
      } else if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        msg = typeof detail === 'string' ? detail : detail.message || msg;
      }
      setError(msg);
    }
    setGenerating(false);
  };

  const handleSaveProject = async () => {
    if (!generatedContent) return;
    setSaving(true);
    try {
      await axios.post(`${API}/projects/`, {
        product_type: selectedType.id,
        title: generatedContent.title || `${selectedType.name} - ${formData.topic}`,
        content: generatedContent,
        design_settings: { tone: formData.tone, style: 'modern' }
      });
      navigate('/dashboard');
    } catch (err) {
      setError('Could not save project.');
    }
    setSaving(false);
  };

  const handleUpdateContent = (newContent) => setGeneratedContent(newContent);
  const handleEditSettings = () => setStep(4);
  const addSection = () => setContentStructure(prev => [...prev, { title: 'New section', desc: 'Describe the content' }]);
  const removeSection = (idx) => setContentStructure(prev => prev.filter((_, i) => i !== idx));
  const updateSection = (idx, field, val) => setContentStructure(prev => prev.map((s, i) => i === idx ? { ...s, [field]: val } : s));

  if (!user || authLoading) return null;

  return (
    <div data-testid="generator-page" className="min-h-screen bg-[#F7F5F2] dark:bg-background">
      {/* Nav */}
      <nav className="bg-white dark:bg-card border-b border-[#d8cfc8]/50 dark:border-border/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <LumeraLogo size="default" />
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-stone-600 font-sans text-sm">
              <Home className="w-4 h-4 mr-1.5" /> Dashboard
            </Button>
            <UserNavMenu size={34} />
          </div>
        </div>
      </nav>

      <div className={`mx-auto px-6 py-10 ${step === 6 ? 'max-w-7xl' : 'max-w-4xl'}`}>
        {/* ── Preview step indicator ── */}
        {previewAccess === 'preview' && step < 6 && !showPaywall && (
          <div data-testid="preview-indicator" className="flex items-center justify-center gap-2 mb-4">
            <Eye className="w-3.5 h-3.5 text-amber-600" />
            <span className="text-xs font-sans font-medium text-amber-700 bg-amber-50 px-3 py-1 rounded-full">
              Free preview · Step {Math.min(step, previewSteps)} of {previewSteps}
            </span>
          </div>
        )}

        {/* ── Premium Paywall ── */}
        {showPaywall && (
          <div className="py-8">
            <PremiumPaywall
              product={previewProduct}
              productSlug={productSlug}
              previewStep={step}
              totalPreviewSteps={previewSteps}
            />
          </div>
        )}

        {/* Progress — 6 steps */}
        {step < 6 && !showPaywall && (
          <div className="flex items-center justify-center gap-1.5 mb-10">
            {stepLabels.slice(0, 5).map((label, i) => {
              const stepNum = i + 1;
              const isLocked = previewAccess === 'preview' && stepNum > previewSteps;
              return (
                <React.Fragment key={label}>
                  <button
                    onClick={() => { if (step > stepNum) handleStepChange(stepNum); }}
                    className={`flex items-center gap-1.5 ${step > stepNum && !isLocked ? 'cursor-pointer' : ''} ${step > stepNum ? 'text-accent' : step === stepNum ? 'text-primary' : 'text-stone-300'}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-sans font-semibold ${
                      isLocked ? 'bg-stone-100 text-stone-300' : step > stepNum ? 'bg-accent text-white' : step === stepNum ? 'bg-primary text-white' : 'bg-stone-100 text-stone-400'
                    }`}>
                      {isLocked ? <Lock className="w-3 h-3" /> : step > stepNum ? <CheckCircle2 className="w-3.5 h-3.5" /> : stepNum}
                    </div>
                    <span className="hidden md:inline text-xs font-sans font-medium">{label}</span>
                  </button>
                  {i < 4 && <div className={`w-5 md:w-10 h-0.5 ${step > stepNum ? 'bg-accent' : 'bg-stone-200'}`} />}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {error && step < 6 && !showPaywall && (
          <div data-testid="generator-error" className="bg-red-50 border border-red-200 text-red-700 text-sm font-sans rounded-xl px-4 py-3 mb-6">
            {error}
          </div>
        )}

        {/* Autosave status */}
        {productSlug && step >= 1 && step <= 5 && (
          <div className="flex justify-end mb-2">
            <AutosaveIndicator dependencies={[formData, contentStructure, step]} enabled={!!productSlug} />
          </div>
        )}

        {/* ═══ Step 1: Describe your idea ═══ */}
        {step === 1 && !showPaywall && (
          <div data-testid="step-1" className="animate-fade-in-up">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900 mb-2">What should the product be about?</h2>
            <p className="text-stone-500 font-sans mb-8">Describe your idea — AI helps you from here.</p>
            <div className="bg-white rounded-2xl border border-stone-100 p-6 md:p-8 space-y-6">
              <div>
                <Label className="text-sm font-sans font-medium text-stone-700 mb-1.5 block">
                  Topic / idea <span className="text-red-400">*</span>
                </Label>
                <Textarea
                  data-testid="topic-input"
                  value={formData.topic}
                  onChange={(e) => setFormData(prev => ({ ...prev, topic: e.target.value }))}
                  placeholder="e.g. &quot;How to start a profitable Etsy shop&quot; or &quot;12-week workout program for beginners&quot;"
                  rows={3}
                  className="bg-stone-50 border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl px-4 py-3"
                />
              </div>
              <div>
                <Label className="text-sm font-sans font-medium text-stone-700 mb-1.5 block">Challenge (optional)</Label>
                <Input
                  data-testid="painpoint-input"
                  value={formData.painPoint}
                  onChange={(e) => setFormData(prev => ({ ...prev, painPoint: e.target.value }))}
                  placeholder="What problem does the product solve? e.g. &quot;People don't know where to start&quot;"
                  className="bg-stone-50 border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl px-4 py-3"
                />
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <Button data-testid="step1-next-btn" onClick={() => { if (formData.topic.trim()) { setError(''); handleStepChange(2); } else setError('Enter a topic for the product.'); }}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 font-sans font-semibold shadow-lg">
                Next <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* ═══ Step 2: Target Audience ═══ */}
        {step === 2 && !showPaywall && (
          <div data-testid="step-2" className="animate-fade-in-up">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900 mb-2">Who is the target audience?</h2>
            <p className="text-stone-500 font-sans mb-8">Who will use this product?</p>
            <div className="bg-white rounded-2xl border border-stone-100 p-6 md:p-8 space-y-6">
              <div>
                <Label className="text-sm font-sans font-medium text-stone-700 mb-1.5 block">
                  Target audience <span className="text-red-400">*</span>
                </Label>
                <Input
                  data-testid="audience-input"
                  value={formData.audience}
                  onChange={(e) => setFormData(prev => ({ ...prev, audience: e.target.value }))}
                  placeholder="e.g. &quot;Women 25-40 who want better control of their finances&quot;"
                  className="bg-stone-50 border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl px-4 py-3"
                />
                {selectedType && (
                  <SuggestionChips
                    suggestions={fieldSuggestions[selectedType.id]?.audiences}
                    onSelect={(val) => setFormData(prev => ({ ...prev, audience: val }))}
                    currentValue={formData.audience}
                  />
                )}
              </div>
            </div>
            <div className="flex justify-between mt-6">
              <Button variant="ghost" onClick={() => setStep(1)} className="font-sans font-medium text-stone-600">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button data-testid="step2-next-btn" onClick={() => { if (formData.audience.trim()) { setError(''); handleStepChange(3); } else setError('Enter the target audience.'); }}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 font-sans font-semibold shadow-lg">
                Next <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* ═══ Step 3: Product Type ═══ */}
        {step === 3 && !showPaywall && (
          <div data-testid="step-3" className="animate-fade-in-up">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900 mb-2">Which product type?</h2>
            <p className="text-stone-500 font-sans mb-8">Choose the format that best fits your idea.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {productTypeConfig.map((type) => (
                <button key={type.id} data-testid={`product-type-${type.id}`}
                  onClick={() => { setSelectedType(type); setContentStructure((contentStructureTemplates[type.id] || []).map(s => ({ ...s }))); }}
                  className={`text-left p-4 rounded-2xl border-2 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${
                    selectedType?.id === type.id ? 'border-primary bg-primary/5' : 'border-stone-100 bg-white hover:border-primary/30'
                  }`}>
                  <type.icon className="w-5 h-5 text-primary mb-2" strokeWidth={1.5} />
                  <h3 className="font-sans font-semibold text-stone-800 text-sm mb-0.5">{type.name}</h3>
                  <p className="text-xs font-sans text-stone-500">{type.desc}</p>
                </button>
              ))}
            </div>
            <div className="flex justify-between mt-6">
              <Button variant="ghost" onClick={() => setStep(2)} className="font-sans font-medium text-stone-600">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button data-testid="step3-next-btn" onClick={() => {
                if (!selectedType) { setError('Select a product type.'); return; }
                setError('');
                // If this product type has a customization schema, show mode chooser
                if (hasCustomization(selectedType.id)) { handleStepChange('mode-select'); return; }
                handleStepChange(4);
              }}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 font-sans font-semibold shadow-lg">
                Next <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* ═══ Mode Selection: Create for me / Customize ═══ */}
        {step === 'mode-select' && selectedType && !showPaywall && (
          <div data-testid="mode-select" className="animate-fade-in-up">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900 mb-2">How do you want to create the product?</h2>
              <p className="text-sm font-sans text-stone-500">You can always customize more afterwards.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {/* Create for me */}
              <button
                data-testid="mode-quick"
                onClick={() => navigate(`/customize?type=${selectedType.id}&mode=quick&topic=${encodeURIComponent(formData.topic || '')}${productSlug ? `&product=${productSlug}` : ''}`)}
                className="group text-left p-6 rounded-2xl border-2 border-stone-200 bg-white hover:border-primary/40 hover:shadow-lg transition-all duration-200"
              >
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/15 transition-colors">
                  <Sparkles className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-serif font-bold text-stone-900 text-lg mb-1.5">Create for me</h3>
                <p className="text-sm font-sans text-stone-500 leading-relaxed">
                  Answer a few questions. Lumera makes smart choices for the rest and creates the product for you.
                </p>
                <p className="text-xs font-sans text-primary font-semibold mt-3 group-hover:underline">
                  Fastest way to a finished product →
                </p>
              </button>

              {/* Tilpass selv */}
              <button
                data-testid="mode-customize"
                onClick={() => navigate(`/customize?type=${selectedType.id}&mode=customize&topic=${encodeURIComponent(formData.topic || '')}${productSlug ? `&product=${productSlug}` : ''}`)}
                className="group text-left p-6 rounded-2xl border-2 border-stone-200 bg-white hover:border-primary/40 hover:shadow-lg transition-all duration-200"
              >
                <div className="w-12 h-12 rounded-2xl bg-stone-100 flex items-center justify-center mb-4 group-hover:bg-primary/10 transition-colors">
                  <Wand2 className="w-6 h-6 text-stone-600 group-hover:text-primary transition-colors" />
                </div>
                <h3 className="font-serif font-bold text-stone-900 text-lg mb-1.5">Customize yourself</h3>
                <p className="text-sm font-sans text-stone-500 leading-relaxed">
                  Choose characters, appearance, illustration style, colors and more. Full creative control.
                </p>
                <p className="text-xs font-sans text-stone-600 font-semibold mt-3 group-hover:text-primary group-hover:underline">
                  Build the product exactly as you want →
                </p>
              </button>
            </div>

            <div className="flex justify-center mt-6">
              <Button variant="ghost" onClick={() => setStep(3)} className="font-sans font-medium text-stone-600">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            </div>
          </div>
        )}

        {/* ═══ Step 4: Style & Tone ═══ */}
        {step === 4 && !showPaywall && (
          <div data-testid="step-4" className="animate-fade-in-up">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900 mb-2">Choose style and tone</h2>
            <p className="text-stone-500 font-sans mb-8">Customize the look and feel of your product.</p>
            <div className="bg-white rounded-2xl border border-stone-100 p-6 md:p-8 space-y-6">
              {/* Tone */}
              <div>
                <Label className="text-sm font-sans font-medium text-stone-700 mb-3 block">Tone</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {toneOptions.map((tone) => (
                    <button key={tone.value} data-testid={`tone-${tone.value}`}
                      onClick={() => setFormData(prev => ({ ...prev, tone: tone.value }))}
                      className={`p-3 rounded-xl border-2 text-sm font-sans font-medium transition-all ${
                        formData.tone === tone.value ? 'border-primary bg-primary/5 text-primary' : 'border-stone-100 text-stone-600 hover:border-primary/30'
                      }`}>
                      {tone.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Language */}
              <div>
                <Label className="text-sm font-sans font-medium text-stone-700 mb-3 block">Language</Label>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                  {[
                    { value: 'no', label: 'Norsk' }, { value: 'en', label: 'English' },
                    { value: 'sv', label: 'Svenska' }, { value: 'da', label: 'Dansk' },
                    { value: 'es', label: 'Español' }, { value: 'de', label: 'Deutsch' },
                    { value: 'fr', label: 'Français' }, { value: 'nl', label: 'Nederlands' }
                  ].map((lang) => (
                    <button key={lang.value} data-testid={`lang-${lang.value}`}
                      onClick={() => setFormData(prev => ({ ...prev, language: lang.value }))}
                      className={`p-3 rounded-xl border-2 text-sm font-sans font-medium transition-all ${
                        formData.language === lang.value ? 'border-primary bg-primary/5 text-primary' : 'border-stone-100 text-stone-600 hover:border-primary/30'
                      }`}>
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Output Size */}
              <div>
                <Label className="text-sm font-sans font-medium text-stone-700 mb-3 block">Format</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {[
                    { value: 'a4-document', label: 'A4 Document', size: '210 × 297mm' },
                    { value: 'us-letter', label: 'US Letter', size: '8.5 × 11"' },
                    { value: 'instagram-post', label: 'Instagram Post', size: '1080 × 1080' },
                    { value: 'instagram-story', label: 'Instagram Story', size: '1080 × 1920' },
                    { value: 'pinterest-pin', label: 'Pinterest Pin', size: '1000 × 1500' },
                  ].map((sz) => (
                    <button key={sz.value} data-testid={`size-${sz.value}`}
                      onClick={() => setFormData(prev => ({ ...prev, outputSize: sz.value }))}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${
                        formData.outputSize === sz.value ? 'border-primary bg-primary/5' : 'border-stone-100 hover:border-primary/30'
                      }`}>
                      <span className={`text-xs font-sans font-semibold block ${formData.outputSize === sz.value ? 'text-primary' : 'text-stone-700'}`}>{sz.label}</span>
                      <span className="text-[10px] font-sans text-stone-400 block mt-0.5">{sz.size}</span>
                    </button>
                  ))}
                </div>
              </div>
              {/* Length */}
              <div>
                <Label className="text-sm font-sans font-medium text-stone-700 mb-3 block">Length</Label>
                <div className="grid grid-cols-3 gap-3">
                  {[{ value: 'short', label: 'Short' }, { value: 'medium', label: 'Medium' }, { value: 'long', label: 'Detailed' }].map((len) => (
                    <button key={len.value} data-testid={`length-${len.value}`}
                      onClick={() => setFormData(prev => ({ ...prev, length: len.value }))}
                      className={`p-3 rounded-xl border-2 text-sm font-sans font-medium transition-all ${
                        formData.length === len.value ? 'border-primary bg-primary/5 text-primary' : 'border-stone-100 text-stone-600 hover:border-primary/30'
                      }`}>
                      {len.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-between mt-6">
              <Button variant="ghost" onClick={() => setStep(3)} className="font-sans font-medium text-stone-600">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button data-testid="step4-next-btn" onClick={() => { setError(''); handleStepChange(5); }}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 font-sans font-semibold shadow-lg">
                Next <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* ═══ Step 5: Content Structure ═══ */}
        {step === 5 && !showPaywall && (
          <div data-testid="step-5" className="animate-fade-in-up">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900 mb-2">Choose content and structure</h2>
            <p className="text-stone-500 font-sans mb-4">AI has suggested a structure for your <strong>{selectedType?.name?.toLowerCase()}</strong>. You can customize it.</p>

            <div className="bg-white rounded-2xl border border-stone-100 p-6 md:p-8">
              {/* Summary of choices */}
              <div className="flex flex-wrap gap-2 mb-6 p-3 rounded-xl bg-stone-50 border border-stone-100">
                <span className="text-xs font-sans text-stone-500"><strong className="text-stone-700">Topic:</strong> {formData.topic.substring(0, 50)}{formData.topic.length > 50 ? '...' : ''}</span>
                <span className="text-xs font-sans text-stone-300">•</span>
                <span className="text-xs font-sans text-stone-500"><strong className="text-stone-700">Type:</strong> {selectedType?.name}</span>
                <span className="text-xs font-sans text-stone-300">•</span>
                <span className="text-xs font-sans text-stone-500"><strong className="text-stone-700">Tone:</strong> {toneOptions.find(t => t.value === formData.tone)?.label}</span>
              </div>

              <div className="space-y-2">
                {contentStructure.map((section, idx) => (
                  <div key={idx} data-testid={`section-${idx}`} className="flex items-start gap-2 p-3 rounded-xl border border-stone-100 bg-white hover:border-primary/20 transition-colors group">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-sans font-bold flex-shrink-0 mt-0.5">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <input
                        type="text"
                        value={section.title}
                        onChange={(e) => updateSection(idx, 'title', e.target.value)}
                        className="text-sm font-sans font-semibold text-stone-800 bg-transparent border-none outline-none w-full"
                      />
                      <input
                        type="text"
                        value={section.desc}
                        onChange={(e) => updateSection(idx, 'desc', e.target.value)}
                        className="text-xs font-sans text-stone-400 bg-transparent border-none outline-none w-full mt-0.5"
                      />
                    </div>
                    <button onClick={() => removeSection(idx)} className="opacity-0 group-hover:opacity-100 text-stone-300 hover:text-red-400 transition-all p-1">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>

              <button data-testid="add-section-btn" onClick={addSection}
                className="flex items-center gap-2 mt-3 text-sm font-sans text-primary hover:text-primary/80 transition-colors">
                <Plus className="w-4 h-4" /> Add section
              </button>
            </div>

            <div className="flex flex-col items-end gap-3 mt-6">
              {error && (
                <div data-testid="generation-error" className="w-full bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-sm font-sans font-medium text-red-800">{error}</p>
                  <p className="text-xs font-sans text-red-600 mt-1">This can happen when the AI server is busy. Your settings are saved — just press Generate again.</p>
                </div>
              )}
              <div className="flex justify-between w-full">
                <Button variant="ghost" onClick={() => setStep(4)} className="font-sans font-medium text-stone-600">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button data-testid="generate-btn" onClick={handleGenerate} disabled={generating}
                  className={`rounded-full px-8 font-sans font-semibold shadow-lg hover:shadow-xl transition-all ${error ? 'bg-accent text-white hover:bg-accent/90' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}>
                  {generating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                  ) : error ? (
                    <><RefreshCw className="w-4 h-4 mr-2" /> Try again</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> Generate product</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ Step 6: Result ═══ */}
        {step === 6 && generatedContent && (
          <div data-testid="step-6">
            {generatedContent.slides ? (
              <VisualProductView
                content={generatedContent}
                onSave={handleSaveProject}
                onNew={() => { setStep(1); setGeneratedContent(null); setSelectedType(null); setActiveTemplate(null); setLoadedProjectId(null); setContentStructure([]); }}
                saving={saving}
                onUpdateContent={handleUpdateContent}
                onEditSettings={handleEditSettings}
                projectId={loadedProjectId}
                outputSize={formData.outputSize}
                productSlug={productSlug}
              />
            ) : (
              <DocumentProductView
                content={generatedContent}
                productType={selectedType?.id}
                onSave={handleSaveProject}
                onNew={() => { setStep(1); setGeneratedContent(null); setSelectedType(null); setActiveTemplate(null); setLoadedProjectId(null); setContentStructure([]); }}
                saving={saving}
                onUpdateContent={handleUpdateContent}
                onEditSettings={handleEditSettings}
                projectId={loadedProjectId}
                outputSize={formData.outputSize}
                productSlug={productSlug}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Generator;
